import React, { useState, useRef, useMemo } from 'react';
import cx from 'classnames';
import { format, parse } from 'date-fns';
import PropTypes from 'prop-types';

import Link from 'components/consumer/Link';
import encodeBase64 from 'commons/encodeBase64';
import useScreen from 'hooks/useScreen';
import { colorWhite } from 'styles/layout/colors';
import { isOptionMaterial } from 'commons/constants';
import CtaLink from 'components/essentials/Cta/CtaLink';
import IconCurbSide from 'components/icons/iconCurbSide';
import IconUPSLogo from 'components/icons/iconUPSLogo';
import IconFedexLogo from 'components/icons/iconFedexLogo';
import BrandTooltip from 'components/consumer/BrandTooltip';
import PDPAlertBar from 'components/consumer/PDP/PDPAlertBar';
import { getProductURL } from 'components/consumer/cart/utils';
import IconInHomeDelivery from 'components/icons/iconInHomeDelivery';
import IconMapMarkerWithBase from 'components/icons/IconMapMarkerWithBase';
import ProductImage from 'components/consumer/cart/components/ProductImage';
import ProtectionPlanButtonAndModal from 'components/consumer/ProtectionPlanButtonAndModal';
import FabricAttributeTooltip from 'components/consumer/FabricAdditionalAttributes/FabricAttributeTooltip';
import cleaningCodeDescriptions from 'components/consumer/FabricAdditionalAttributes/cleaning-code-desc.json';

const DisplayOrder = ({
  group,
  id,
  index,
  isDone,
  isImpersonatingUser,
  isLoggedIn,
  isOpenTrackTooltip,
  isShipped,
  noFabrics,
  openTrackingTooltip,
  order,
  selectedCartItemId,
  setSelectedCartItemId,
  setSelectedPPState,
  showSignInPageUponSelection,
}) => {
  const { isMobile } = useScreen();
  const orderInfoRef = useRef(null);
  const deliveryInfoRef = useRef(null);
  const trackingInfoRef = useRef(null);
  const [openGloveToolTip, setOpenGloveTooltip] = useState(false);
  const [openCurbsideTooltip, setOpenCurbsideTooltip] = useState(false);

  let deliveredDate = 'TBD';
  const pickLater = group.some(item => item.pickLater);
  const estimatedDate = group[0].estimated_delivery || 'TBD';
  const estimatedLabel = group[0].estimated_delivery_title;

  if (group[0].delivered_date) {
    try {
      deliveredDate = format(
        parse(group[0].delivered_date, 'yyyy-MM-dd HH:mm:ss', new Date()),
        'MMM dd'
      );
    } catch (e) {
      // ignore error
    }
  }

  /**
   * First get order item delivered_date if not present
   * Get order.delivery_date if not present
   * Get order item target_date else null
   */
  const deliveredYrsBefore = useMemo(() => {
    const deliveredOn =
      group[0].delivered_date ??
      order.delivery_date ??
      group[0]?.target_date ??
      null;

    if (deliveredOn) {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      const todaysDate = `${year}-${month}-${day}`;
      return Math.ceil(
        (new Date(todaysDate).getTime() - new Date(deliveredOn).getTime()) /
          (1000 * 60 * 60 * 24 * 365.25)
      );
    }
    return 0;
  }, [group, order.delivery_date]);

  const deliveryDate = isDone ? (
    group[0].is_delivered === '1' ? (
      <>Delivered on: {deliveredDate}</>
    ) : null
  ) : !isShipped && estimatedLabel ? (
    <>
      {estimatedLabel}: {pickLater ? 'TBD' : estimatedDate}
    </>
  ) : null;

  return (
    <div
      key={id}
      className="mb-6 bg-white rounded-lg last:mb-0"
      data-xc="user-order-shipment"
    >
      <div className="p-4 flex items-center justify-between bg-gray-light9">
        <div className="text-base font-bold">Shipment {index + 1}</div>
        <div>
          {order.order_status !== 'Canceled' &&
          (group[0].delivery_method === 'inhome_delivery' ||
            group[0].delivery_method === 'curbside_delivery') ? (
            <>
              {group[0].delivery_method === 'inhome_delivery' ? (
                <div
                  ref={deliveryInfoRef}
                  className="text-sm font-bold pt-[.2rem] text-gray-light2 flex items-center hover:underline [&_span]:px-1 [&_span]:py-0"
                >
                  <IconInHomeDelivery width={18} />{' '}
                  <span
                    role="presentation"
                    onClick={() => setOpenGloveTooltip(!openGloveToolTip)}
                  >
                    In-home delivery
                  </span>{' '}
                  <BrandTooltip
                    title=""
                    parentRef={deliveryInfoRef}
                    isOpenDefault={openGloveToolTip}
                  >
                    Our delivery partners will deliver your order on a
                    pre-scheduled date. The team will move and unpack your order
                    to the room of your choice and dispose of all packaging.
                  </BrandTooltip>
                </div>
              ) : null}

              {group[0].delivery_method === 'curbside_delivery' ? (
                <div
                  ref={deliveryInfoRef}
                  className="text-sm font-bold pt-[.2rem] text-gray-light2 flex items-center hover:underline [&_span]:px-1 [&_span]:py-0"
                >
                  <IconCurbSide width={18} />{' '}
                  <span
                    role="presentation"
                    onClick={() => setOpenCurbsideTooltip(!openCurbsideTooltip)}
                  >
                    Curbside Delivery
                  </span>{' '}
                  <BrandTooltip
                    title=""
                    parentRef={deliveryInfoRef}
                    isOpenDefault={openCurbsideTooltip}
                  >
                    Your order will be delivered right to your doorstep or
                    closest entrance, but won’t be unpacked or assembled.
                  </BrandTooltip>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <div className="p-6 relative" data-xc="user-order-shipment-details">
        <div
          className={cx(
            'flex items-center justify-between max-[1023px]:flex-col max-[1023px]:items-start',
            {
              'lg:w-full lg:-left-6': !deliveryDate,
            }
          )}
        >
          <div className="text-lg font-bold max-[1023px]:mb-3">
            {deliveryDate}
          </div>
          <div className="order-info__group-tracking">
            {!isDone && !pickLater ? (
              group[0].efg_flag === '1' ? (
                group[0].tracking_url && group[0].tracking_carrier ? (
                  <CtaLink
                    className="!p-[0.8rem]"
                    textClassName="!normal-case"
                    size="sm"
                    color="brand"
                    hasArrow={false}
                    styling="solid-button"
                    to={group[0].tracking_url}
                  >
                    {group[0].tracking_carrier?.includes('ups') ? (
                      <IconUPSLogo width={28} height={33} />
                    ) : group[0].tracking_carrier?.includes('fedex') ? (
                      <IconFedexLogo />
                    ) : (
                      group[0].tracking_carrier
                    )}{' '}
                    Tracking
                  </CtaLink>
                ) : (
                  <div
                    ref={trackingInfoRef}
                    className="text-sm font-bold pt-[.2rem] text-gray-light2 flex items-center hover:underline [&_span]:px-1 [&_span]:py-0"
                  >
                    <div
                      role="presentation"
                      onClick={() => openTrackingTooltip(!isOpenTrackTooltip)}
                    >
                      Awaiting Tracking Info
                    </div>{' '}
                    <BrandTooltip
                      title=""
                      parentRef={trackingInfoRef}
                      isOpenDefault={isOpenTrackTooltip}
                    >
                      We are currently awaiting the tracking information from
                      the carrier. Once it has been generated, you’ll see it
                      here. We’ll also send you an email
                    </BrandTooltip>
                  </div>
                )
              ) : order.order_status ? (
                <CtaLink
                  className="!p-[0.8rem]"
                  textClassName="!normal-case"
                  size="sm"
                  color="brand"
                  hasArrow={false}
                  styling="solid-button"
                  to={`/tracking?order_id=${order.order_id}`}
                >
                  {isMobile ? (
                    <IconMapMarkerWithBase color={colorWhite} />
                  ) : null}
                  Build updates & tracking
                </CtaLink>
              ) : null
            ) : null}
            {deliveredYrsBefore > 10 && (
              <div className="text-sm font-bold pt-[.2rem] text-gray-light2 flex items-center hover:underline [&_span]:px-1 [&_span]:py-0">
                Warranty expired
              </div>
            )}
          </div>
        </div>

        {!!noFabrics && (
          <div className="text-base font-bold px-0 py-2.5 text-red">
            {noFabrics} Thank you for your patience.
          </div>
        )}

        {group.map((item, idx) => {
          const productLinkWithOptions = getProductURL({
            productUrlKey: item?.url_key,
            options: item.options,
            pickLater: item.pickLater,
          });

          const internalCleaningCode =
            item.group_cleaning_code === 'WS'
              ? 'S/W'
              : item.group_cleaning_code;

          return (
            <div key={item.id} className="order-info__group-item">
              <div
                className={cx({
                  'mx-0 my-8 [border-bottom:1px_solid_#f1f1f1]': idx !== 0,
                  'm-0 px-0 py-3 [border-bottom:none]': idx === 0,
                })}
              />

              <div className="flex [&_img]:p-4 [&_img]:rounded [&_img]:bg-gray-light7">
                <Link
                  className="order-info__product-img"
                  to={productLinkWithOptions}
                >
                  <ProductImage
                    containerClass="order-info__product-img-inner"
                    product={item}
                    width={100}
                    trim="color"
                    trimPad={20}
                  />
                </Link>

                <div
                  className="ml-[.825rem]"
                  data-xc="user-order-shipment-product-details"
                >
                  <Link
                    className="text-base font-bold text-brand no-underline hover:underline"
                    to={productLinkWithOptions}
                  >
                    {item.name}
                  </Link>

                  <div className="text-xs font-bold text-gray-light2">{`Qty: ${item.quantity}`}</div>

                  <div className="order-info__product-options">
                    {item.options.map(option => (
                      <div
                        key={option.id}
                        className="text-xs font-bold text-gray-light2"
                      >
                        {item.pickLater && isOptionMaterial[option.optionTypeId]
                          ? 'Decide Later'
                          : `${option.name} ${option.optionTypeName}`}
                      </div>
                    ))}
                  </div>

                  {item.group_cleaning_code ? (
                    <div
                      className="text-xs font-bold text-gray-light2"
                      ref={orderInfoRef}
                    >
                      {`Cleaning Code: ${item.group_cleaning_code}`}

                      {!!cleaningCodeDescriptions[internalCleaningCode] && (
                        <span className="mt-0 mx-0 mb-[5px]">
                          <FabricAttributeTooltip
                            title={
                              cleaningCodeDescriptions[internalCleaningCode]
                                .title
                            }
                            description={
                              cleaningCodeDescriptions[internalCleaningCode]
                                .description
                            }
                            parentRef={orderInfoRef}
                          />
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              {order.order_status !== 'Canceled' &&
                order.order_status !== 'Replacement' &&
                (item.applied_protection_plan ||
                  (order.order_status !== 'Delivered' &&
                    item.available_protection_plans?.length > 0)) && (
                  <ProtectionPlanButtonAndModal
                    productId={item.product_id}
                    selectedProtectionPlan={item.applied_protection_plan}
                    eligible_plans={item.available_protection_plans}
                    openModal={() => {
                      if (!item.applied_protection_plan) {
                        setSelectedCartItemId(item.cart_id);
                      }
                    }}
                    isOpen={item.cart_id === selectedCartItemId}
                    close={() => {
                      setSelectedCartItemId(null);
                    }}
                    onSelect={planId => {
                      if (!isImpersonatingUser && planId) {
                        setSelectedPPState({
                          cartItem: item,
                          orderId: order.order_id,
                          selectedPlan: item.available_protection_plans.find(
                            p => p.plan_id === planId
                          ),
                        });
                      }
                    }}
                    showInactiveProtectionPlan
                    forPage="UserDashboardOrdersPage"
                    showSignInPageUponSelection={showSignInPageUponSelection}
                  />
                )}

              <div className="order-info__actions-container">
                {order.order_status === 'Delivered' && !item.review_exists && (
                  <div className="mt-8">
                    <CtaLink
                      color="black"
                      hasArrow={false}
                      styling="solid-button"
                      to={{
                        pathname: '/customer/my-reviews',
                        state: {
                          cart_id: item.cart_id,
                          product_id: item.product_id,
                        },
                      }}
                    >
                      Leave a review
                    </CtaLink>

                    <span className="ml-[.825rem] text-gray-light2 text-sm">
                      Get up to $50 in rewards!
                    </span>
                  </div>
                )}

                {item.pickLater && (
                  <>
                    <PDPAlertBar type="info">
                      <strong>Important: </strong> You must select your product
                      customizations before we can start building your
                      furniture!
                    </PDPAlertBar>

                    <div className="mt-8">
                      <CtaLink
                        color="black"
                        hasArrow={false}
                        styling="solid-button"
                        to={
                          !isLoggedIn
                            ? '/customer/orders'
                            : isImpersonatingUser
                            ? item.url_key
                            : `/edit-order/item/${encodeBase64(
                                item.cart_id
                              )}?order_id=${encodeBase64(order.order_id)}`
                        }
                      >
                        Select Customizations
                      </CtaLink>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

DisplayOrder.propTypes = {
  group: PropTypes.object,
  id: PropTypes.string,
  index: PropTypes.number,
  isDone: PropTypes.bool,
  isImpersonatingUser: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isOpenTrackTooltip: PropTypes.bool,
  isShipped: PropTypes.bool,
  noFabrics: PropTypes.object,
  openTrackingTooltip: PropTypes.func,
  order: PropTypes.object,
  selectedCartItemId: PropTypes.string,
  setSelectedCartItemId: PropTypes.func,
  setSelectedPPState: PropTypes.func,
  showSignInPageUponSelection: PropTypes.bool,
};

export default DisplayOrder;
